import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import ReviewSubmitModal from "@/components/ReviewSubmitModal";
import api from "@/lib/api";

/**
 * Landing-page social-proof block. Two rows:
 *   1. Editorial headline + 5-star trust cue
 *   2. Auto-rotating anonymous testimonial (fictional but plausible; content
 *      lives in i18n so it can be tuned per language without a redeploy of
 *      this component).
 *
 * Counters were intentionally left out — user data volumes are still too
 * modest to broadcast, and the testimonial line is a stronger emotional
 * anchor than "50+ applicants".
 */
export default function SocialProofStrip() {
  const { t } = useT();
  const { user } = useAuth();
  const [idx, setIdx] = useState(0);
  const [approved, setApproved] = useState([]);
  const [showReview, setShowReview] = useState(false);

  // Fetch approved testimonials once. Falls back to editorial placeholders
  // (i18n keys `spQuote1-3Body/Name`) when the list is empty.
  useEffect(() => {
    let alive = true;
    api.get("/testimonials/approved")
      .then((r) => { if (alive) setApproved(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { /* placeholders remain */ });
    return () => { alive = false; };
  }, []);

  const quotes = approved.length > 0
    ? approved.map((tst) => ({
        body: tst.body,
        name: [tst.alias || "Anon", tst.city, tst.role].filter(Boolean).join(" · "),
      }))
    : [1, 2, 3].map((n) => ({
        body: t(`spQuote${n}Body`),
        name: t(`spQuote${n}Name`),
      }));

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % quotes.length), 7000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = quotes[idx];

  return (
    <section
      data-testid="social-proof"
      className="border-y border-black/5 bg-[#F0EFEA]/40"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-8 sm:py-14 grid gap-6 sm:gap-10 md:grid-cols-12 items-start">
        <div className="md:col-span-6">
          <p className="ed-eyebrow flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-0.5 text-amber-500">
              {[0,1,2,3,4].map((i) => (
                <Star key={i} size={11} strokeWidth={0} fill="currentColor" />
              ))}
            </span>
            {t("spEyebrow")}
          </p>
          <p className="font-serif text-xl sm:text-2xl md:text-3xl leading-tight text-neutral-900">
            {t("spHeadline")}
          </p>
        </div>

        <div className="md:col-span-6 md:border-l md:border-neutral-300 md:pl-10">
          <Quote size={18} strokeWidth={1.25} className="text-amber-600 mb-3" />
          <blockquote
            key={idx}
            data-testid="social-proof-quote"
            className="font-serif italic text-lg leading-relaxed text-neutral-800 land-fade"
          >
            “{active.body}”
          </blockquote>
          <p className="mt-4 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-neutral-500">
            — {active.name}
          </p>
          <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex gap-2" aria-hidden="true">
              {quotes.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`social-proof-dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`w-6 h-0.5 transition-colors ${i === idx ? "bg-neutral-900" : "bg-neutral-300 hover:bg-neutral-500"}`}
                  aria-label={`quote ${i + 1}`}
                />
              ))}
            </div>
            {user && (
              <button
                data-testid="share-review-cta"
                type="button"
                onClick={() => setShowReview(true)}
                className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-neutral-300 text-neutral-700 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors px-2.5 py-1 inline-flex items-center gap-1.5"
              >
                <Quote size={11} strokeWidth={1.75} /> {t("spShareCta")}
              </button>
            )}
          </div>
        </div>
      </div>
      <ReviewSubmitModal open={showReview} onClose={() => setShowReview(false)} />
    </section>
  );
}
